import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import {
  IonContent,
  ModalController,
  ViewDidEnter,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  LOCAL_STORAGE_KEYS,
  PanAppState,
  PlatformService,
  Tracking,
} from '@panamax/app-state';
import { Customer } from '@usf/customer-types';
import {
  Observable,
  Subscription,
  combineLatest,
  firstValueFrom,
  of,
} from 'rxjs';
import { filter, first, map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { MessageTypeEnum } from '../ngrx-message/constants/messageTypeEnum';
import { Message } from '../ngrx-message/models/client/message';
import { MessageStoreService } from '../ngrx-message/services/message/message-store.service';
import { NotificationsService } from '../notifications/services/notifications.service';
import { OrderService } from '../order/services/order.service';
import { HelpModalDesktopComponent } from '../shared/components/help-modal/help-modal-desktop/help-modal-desktop.component';
import { HelpModalTouchComponent } from '../shared/components/help-modal/help-modal-touch/help-modal-touch.component';
import { ModalWithNavComponent } from '../shared/components/modal-with-nav/modal-with-nav.component';
import { PageTitle } from '../shared/constants/page-titles.enum';
import { PATHS } from '../shared/constants/paths';
import { FEATURES } from '../shared/constants/splitio-features';
import { ToastMessages } from '../shared/constants/toast-messages.enum';
import { ModalService } from '../shared/services/modal/modal.service';
import { HomePageViewModel } from './models/home-page-view-model.model';
import { InteractBannerInfo } from './models/interact-banner-info.model';
import { HomeService } from './services/home.service';
import { UpdateMoxeService } from '../shared/services/update-moxe.service';
import { OrderItemService } from '@shared/services/order-item.service';
import { QuantityUpdate } from '@shared/models/quantity-update';
import { MerchandisingProductViewModel } from '@shared/models/merchandising-product-view.model';
import { RECENTLY_VIEWED } from '@shared/constants/home-constants';
import {
  MyOrdersStoreService,
  OrderHeader,
  OrderStatus,
} from '@usf/ngrx-order';
import { RecentOrderTileViewModel } from '@order/models/recent-order-tile-view-model';
import { OrderTracingService } from '@order/tracking/tracing/order-tracing.service';
import { OrderSubmittedAnalyticsService } from '@order/pages/order-submitted/tracking/analytics/order-submitted-analytics.service';
import { OrderSyncingComponent } from '@shared/components/popovers/order-syncing/order-syncing.component';
import { MessageHandlerService } from '@shared/services/message/message-handler.service';
import { CustomerOrdersViewModel } from '@shared/models/my-orders-view.model';
import { CustomerSwitchStatus } from '@app/ngrx-customer/constants/customer-switch-status';
import { UserService } from '@app/user/services';
import {
  PERSONALIZATION_TYPE,
  ProductAnalyticsService,
} from '@usf/ngrx-product';
import { PdpLnkSrc } from '@shared/constants/pdp-lnksrc.enum';
import { MerchandisingService } from '@shared/services/merchandising/merchandising.service';
import { ProductService } from '@shared/services/product/product.service';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { OrderAnalyticsService } from '@order/tracking/analytics/order-analytics.service';
import { CustomerRebatesService } from '@shared/services/customer-rebates/customer-rebates.service';
import { CustomerRebate } from '@usf/product-types';
import { PersonalizationService } from '@shared/services/personalization/personalization.service';
@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
})
export class HomePage implements ViewWillEnter, ViewDidEnter, ViewWillLeave {
  static MAX_DATA_REFRESH_CALLS = 3;
  static MAX_WMT_MONGO_REFRESH_CALLS = 6;
  static DATA_REFRESH_INTERVAL = 5 * 60 * 1000;
  readonly newScoopProducts = environment.allScoop;
  readonly PATHS = PATHS;

  scoopPreviewBannerFlag = FEATURES.split_global_seller_scoop_preview.name;
  avendraForGoodBanner = FEATURES.split_global_avendraforgood_banner.name;
  rebatesBannerFlag = FEATURES.split_dynamic_rebates_banner.name;

  @ViewChild(IonContent, { static: false }) content: IonContent;
  @ViewChild('interactBannerContainer') interactBannerContainer: any;

  featureFlagInteractBanners$: Subscription = new Subscription();
  customersSub$: Subscription = new Subscription();
  getInteractBannersSub$: Subscription = new Subscription();
  loadSubmittedOrderSub$: Subscription = new Subscription();
  pushActionPerformedUserSub$: Subscription = new Subscription();
  selectedCustomerSub$: Subscription = new Subscription();
  recentlyViewedProductNumbersSub$: Subscription;
  recentlyViewedSeenProductsSub$: Subscription;
  userCustSub$: Subscription;
  customizationSub$: Subscription;
  recentOrderVM$: Observable<RecentOrderTileViewModel>;
  customerOrderVM$: Observable<CustomerOrdersViewModel>;

  homePageViewModel$: Observable<HomePageViewModel>;
  tracking$: Observable<HomePageViewModel>;
  isInternalUser$: Observable<boolean>;

  interactBanners: InteractBannerInfo[];
  isInteractBannersFeatureFlagEnabled = false;
  userHasAvendraForGoodCustomization = false;
  trackedInteractBanners = [];
  currentUserKind: string;

  recentlyViewedFeatureFlag = false;
  recentlyViewedProducts$: Observable<MerchandisingProductViewModel[]>;
  incentivePrograms$: Observable<CustomerRebate[]>;
  recentlyViewedIsTracked = false;
  RECENTLY_VIEWED_FEATURE_NAME = RECENTLY_VIEWED;
  recentlyViewedLnkSrc = PdpLnkSrc.recentlyViewed;

  selectedCustomer: Customer;

  refreshIntervalWMT: ReturnType<typeof setInterval>;
  refreshIntervalWMTCount = 0;
  sessionSubscription$: Subscription;
  scrollTimer: ReturnType<typeof setTimeout>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private modalController: ModalController,
    readonly panAppState: PanAppState,
    private updateMoxeService: UpdateMoxeService,
    private customerStoreService: CustomerStoreService,
    private homeService: HomeService,
    private messageStoreService: MessageStoreService,
    private modalService: ModalService,
    private notificationService: NotificationsService,
    private orderService: OrderService,
    readonly platformService: PlatformService,
    private translateService: TranslateService,
    private orderItemService: OrderItemService,
    private myOrdersStoreService: MyOrdersStoreService,
    private orderTracingService: OrderTracingService,
    private orderSubmittedAnalyticsService: OrderSubmittedAnalyticsService,
    private messageHandlerService: MessageHandlerService,
    private userService: UserService,
    private productAnalyticsService: ProductAnalyticsService,
    private merchandisingService: MerchandisingService,
    private productService: ProductService,
    private personalizationService: PersonalizationService,
    private readonly toggleNewSearchService: ToggleNewSearchService,
    private customerRebatesService: CustomerRebatesService,
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params.ecomUserId && params.userName) {
        alert(this.translateService.instant('i18n.home.alreadyLoggedIn'));
      }
    });
  }

  ionViewWillEnter() {
    this.updateMoxeService.executeBackgroundRefresh();
    this.platformService.onResize(new Event('resize'));
    this.showCacheClearToastMessage;
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.home),
    );
    this.homePageViewModel$ = this.homeService.getHomePageViewModel$();
    this.recentOrderVM$ = this.orderService.loadRecentOrderVM();
    this.isInternalUser$ = this.homeService.isInternalUser();
    this.refreshIntervalWMTCount = 0;
    this.refreshIntervalWMT = setInterval(() => {
      this.refreshIntervalWMTCount++;
      if (this.refreshIntervalWMTCount > HomePage.MAX_WMT_MONGO_REFRESH_CALLS) {
        clearInterval(this.refreshIntervalWMT);
      } else {
        this.myOrdersStoreService.retrieveCustomerOrders();
        this.homePageViewModel$ = this.homeService.getHomePageViewModel$();
      }
    }, HomePage.DATA_REFRESH_INTERVAL);
    if (this.content) {
      this.content.scrollToTop();
    }

    const showToast =
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.cacheCleared)) ||
      false;
    if (showToast) {
      this.showCacheClearToastMessage();
      localStorage.removeItem(LOCAL_STORAGE_KEYS.cacheCleared);
    }

    this.registerDevice();
    this.notificationService.checkMissedNotification();
    this.featureFlagInteractBanners$ = this.panAppState
      ?.feature$([FEATURES.split_global_interact_banners.name])
      ?.subscribe(async on => {
        if (on) {
          this.isInteractBannersFeatureFlagEnabled = true;
          this.getInteractBannersSub$ = this.homeService
            ?.getInteractBanners$()
            ?.pipe(filter(ib => !!ib))
            ?.subscribe(ib => {
              const scoopBanner = ib.find(i => i.zone === 3);
              this.interactBanners = ib.filter(i => i.zone === 1);
              this.interactBanners.push(scoopBanner);
            });
        }
      });
    this.userCustSub$ = combineLatest([
      this.panAppState.user$.pipe(
        filter(user => !!user && !!user.userKind),
        take(1),
      ),
      this.homeService.getSelectedCustomer()?.pipe(filter(cust => !!cust)) ||
        of(null),
      this.userService.userCustomizations$()?.pipe(
        filter(custom => !!custom),
        take(1),
      ),
    ])
      .pipe(
        map(([user, selectedCustomer, userCustomizations]) => {
          return {
            user,
            selectedCustomer,
            userCustomizations,
          };
        }),
      )
      .subscribe(res => {
        this.currentUserKind = res?.user?.userKind;
        this.selectedCustomer = res?.selectedCustomer;
        this.userHasAvendraForGoodCustomization =
          res?.userCustomizations?.profileName?.toLowerCase() ===
          'avendra for good';
      });

    this.sessionSubscription$ = this.customerStoreService
      .customerSwitchStatus$()
      .pipe(
        filter(status => status === CustomerSwitchStatus.complete),
        switchMap(value => {
          return combineLatest([
            this.panAppState.feature$([
              FEATURES.split_division_recently_viewed.name,
            ]),
            this.panAppState.feature$([
              FEATURES.split_dynamic_rebates_banner.name,
            ]),
            this.panAppState.feature$([
              FEATURES.split_dynamic_recently_viewed_new_model.name,
            ]),

            this.userService.showRebates$(),
          ]);
        }),
      )
      .subscribe(
        ([
          isRecentlyViewedEnabled,
          isRebatesFlagEnabled,
          isRecentlyViewedNewModelEnabled,
          showRebates,
        ]) => {
          if (isRecentlyViewedEnabled) {
            this.fetchAndLoadRecentlyViewed(isRecentlyViewedNewModelEnabled);
            this.loadRecentlyViewedProducts(isRecentlyViewedNewModelEnabled);
            this.recentlyViewedFeatureFlag = true;
          } else {
            this.recentlyViewedFeatureFlag = false;
          }
          if (showRebates && isRebatesFlagEnabled) {
            this.incentivePrograms$ =
              this.customerRebatesService.getIncentivePrograms();
          }
        },
      );
  }

  ionViewDidEnter() {
    this.tracking$ = this.homePageViewModel$.pipe(
      filter(vm => vm?.trackingIsReady),
      first(),
      tap(vm => {
        this.homeService.trackPageLoad(
          vm?.recommendedDetails
            ? 'TMN-' + vm?.recommendedDetails.noteId
            : null,
          vm?.deliveryDetails ? 1 : null,
          vm?.dealsFlag ? 1 : null,
          vm?.newsFlag ? 1 : null,
        );
      }),
    );
  }

  showCacheClearToastMessage() {
    this.messageStoreService.upsertMessage({
      stack: null,
      watermark: new Date().toUTCString(),
      read: false,
      type: MessageTypeEnum.success,
      display: ToastMessages.securityAndPrivacy,
      toast: true,
    } as Message);
  }

  registerDevice() {
    if (
      this.platformService.platformType !==
      this.platformService.platformEnum.desktop
    ) {
      this.notificationService.registerDevice();
    }
  }

  async createNewOrder(
    recentOrderViewModel?: RecentOrderTileViewModel,
    continueClicked?: boolean,
  ) {
    if (!recentOrderViewModel || continueClicked) {
      this.orderService.createNewOrder();
      this.routeTo(PATHS.LISTS);
    } else {
      if (await this.shouldShowCartSyncingPopover()) {
        await this.presentCartSyncingModal(recentOrderViewModel, true);
      } else {
        this.orderService.createNewOrder(undefined, PageTitle.home);
        this.routeTo(PATHS.LISTS);
      }
    }
  }

  routeTo(path: string, queryParams = {}) {
    this.router.navigate([path], { queryParams });
  }

  getQueryParams(query: any) {
    const finalParams = {};
    query.forEach(key => {
      var tokens = key.split('=');
      finalParams[tokens[0]] = tokens[1];
    });
    return finalParams;
  }

  // need to be able to mock this method in unit tests by creating a wrapper
  getTransactionId() {
    return uuidv4();
  }

  ionViewWillLeave(): void {
    if (this.featureFlagInteractBanners$) {
      this.featureFlagInteractBanners$.unsubscribe();
    }
    if (this.customersSub$) {
      this.customersSub$.unsubscribe();
    }
    if (this.getInteractBannersSub$) {
      this.getInteractBannersSub$.unsubscribe();
    }
    if (this.loadSubmittedOrderSub$) {
      this.loadSubmittedOrderSub$.unsubscribe();
    }
    if (this.pushActionPerformedUserSub$) {
      this.pushActionPerformedUserSub$.unsubscribe();
    }
    if (this.selectedCustomerSub$) {
      this.selectedCustomerSub$.unsubscribe();
    }
    if (this.refreshIntervalWMT) {
      clearInterval(this.refreshIntervalWMT);
    }
    if (this.recentlyViewedProductNumbersSub$) {
      this.recentlyViewedProductNumbersSub$.unsubscribe();
    }
    if (this.sessionSubscription$) {
      this.sessionSubscription$.unsubscribe();
    }
    if (this.recentlyViewedSeenProductsSub$) {
      this.recentlyViewedSeenProductsSub$.unsubscribe();
    }
    if (this.userCustSub$) {
      this.userCustSub$.unsubscribe();
    }
    clearTimeout(this.scrollTimer);
  }

  async openHelpModal() {
    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: HelpModalTouchComponent,
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: HelpModalDesktopComponent,
        },
        'modal-desktop-size-xl',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  loadRecentlyViewedProducts(isRecentlyViewedNewModelEnabled: boolean) {
    if (isRecentlyViewedNewModelEnabled) {
      const productSource$ =
        this.personalizationService.loadPersonalizationProducts$(
          PERSONALIZATION_TYPE.recentlyViewed,
        );

      this.recentlyViewedProducts$ = productSource$.pipe(
        tap(products => {
          if (!this.recentlyViewedIsTracked && products) {
            const productsTracking = products.map(p => ({
              attributes: p.trackingAttributes,
              divisionApn: `${p?.summary?.divisionNumber}-${p?.productNumber}`,
              merchFeatureView: '1',
              merchFeatureName: RECENTLY_VIEWED,
            }));

            this.productAnalyticsService.trackMerchCarouselLoaded(
              productsTracking,
            );
            this.recentlyViewedIsTracked = true;
          }
        }),
      );
    }
  }

  onQuantityUpdate(quantityUpdate: QuantityUpdate, index: number) {
    const trackingData: Tracking = {
      analytics: {
        data: {
          products: [
            {
              addMethod: RECENTLY_VIEWED,
              merchFeatureName: RECENTLY_VIEWED,
            },
          ],
          results: {
            resultRank: index + 1,
          },
        },
      },
    };

    this.orderItemService.changeQuantity(
      quantityUpdate.productNumber,
      quantityUpdate.quantity,
      quantityUpdate.isEaches,
      trackingData,
    );
  }

  onScrollStart() {
    clearTimeout(this.scrollTimer);
  }

  onScrollEnd() {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      this.trackVisibleInteractBanners();
    }, 3000);
  }

  trackVisibleInteractBanners() {
    let interactBannersToTrack = [];
    const interactBanners = Array.from(
      this.interactBannerContainer?.nativeElement?.children,
    ) as any[];
    if (Array.isArray(interactBanners)) {
      interactBanners?.forEach(banner => {
        if (this.isElementInViewport(banner)) {
          if (this.trackedInteractBanners?.indexOf(banner) === -1) {
            interactBannersToTrack.push(
              banner.children[0]?.src?.substring(
                banner.children[0]?.src?.toLowerCase().indexOf('interact/'),
              ),
            );
            this.trackedInteractBanners.push(banner);
          }
        }
      });
    }
    if (interactBannersToTrack && interactBannersToTrack?.length > 0) {
      this.homeService.trackInteractBannersInViewport(
        interactBannersToTrack?.join(','),
      );
    }
  }

  isElementInViewport(el: any) {
    var rect = el?.getBoundingClientRect();
    const browserSize = this.platformService?.getBrowserDimensions();
    const top =
      this.platformService.platformType ===
      this.platformService.platformEnum.desktop
        ? 160
        : 0;
    return (
      rect.top >= top &&
      rect.left >= 0 &&
      rect.bottom <= browserSize.height &&
      rect.right <= browserSize.width
    );
  }

  handleRecentOrderButtonClick(recentOrderViewModel: RecentOrderTileViewModel) {
    const orderHeader = recentOrderViewModel?.recentOrder?.orderHeader;
    const orderStatus =
      recentOrderViewModel?.recentOrder?.orderHeader?.orderStatus;

    switch (orderStatus) {
      case OrderStatus.SUBMITTED:
      case OrderStatus.SUBMITTED_CREDIT_HOLD:
        recentOrderViewModel.isEditable
          ? this.switchToRecentOrder(recentOrderViewModel)
          : this.createNewOrder(recentOrderViewModel);
        break;

      case OrderStatus.SUBMITTED_WITH_EXCEPTIONS:
        this.recentOrderResolveExceptions(orderHeader);
        break;

      default:
        this.orderTracingService.traceGoToCartClickedOnHomepage(orderHeader);
        this.switchToRecentOrder(recentOrderViewModel);
    }
  }

  async switchToRecentOrder(recentOrderViewModel: RecentOrderTileViewModel) {
    const showPopover = await this.shouldShowCartSyncingPopover();
    if (showPopover) {
      await this.presentCartSyncingModal(recentOrderViewModel, false);
    } else {
      this.modifyRecentOrder(recentOrderViewModel);
    }
  }

  modifyRecentOrder(recentOrderViewModel: RecentOrderTileViewModel) {
    const { recentOrder, currentCustomer } = recentOrderViewModel;
    if (recentOrder.orderHeader?.orderStatus !== OrderStatus.IN_PROGRESS) {
      this.orderService
        .getProductsForTracing$(recentOrder?.orderItems)
        .pipe(
          take(1),
          tap(products =>
            this.orderTracingService.trackModifyButtonClickFromHomePageTile(
              recentOrder?.orderHeader,
              recentOrder?.orderItems,
              products,
            ),
          ),
        )
        .subscribe();
    }
    const analyticsOrderId = OrderAnalyticsService.getAnalyticsOrderId(
      recentOrder?.orderHeader,
    );
    this.orderSubmittedAnalyticsService.trackTileEditOrderButtonClick(
      analyticsOrderId,
      recentOrder?.orderHeader?.orderStatus,
    );
    this.panAppState.user$.pipe(take(1)).subscribe(user => {
      if (user?.tmUser === 'Y') {
        if (!!recentOrder?.orderHeader?.tandemOrderNumber) {
          this.myOrdersStoreService.retrieveOrderDetails(
            recentOrder?.orderHeader?.tandemOrderNumber,
            true,
          );
        }
      }
    });
    this.orderService.makeOrderCurrentOrder(recentOrder, currentCustomer);
    this.routeTo(PATHS.ORDER);
  }

  recentOrderResolveExceptions(orderHeader: OrderHeader) {
    this.orderTracingService.traceResolveButtonClickedOnHomepage(orderHeader);
    this.messageHandlerService.createSpinnerModal().then(() => {
      this.orderService.routeResolveExceptionsHomePage(orderHeader?.orderId);
    });
  }

  async shouldShowCartSyncingPopover(): Promise<boolean> {
    try {
      const showPopover = await firstValueFrom(
        this.orderService.shouldShowCartSyncingPopover$().pipe(take(1)),
      );
      return !!showPopover;
    } catch (e) {
      return;
    }
  }

  async presentCartSyncingModal(
    recentOrderViewModel: RecentOrderTileViewModel,
    isCreateNewOrder: boolean,
  ) {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      OrderSyncingComponent,
      { customerSwitch: false, orderSwitch: true },
      'simple-action-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then(resp => {
      if (resp?.data?.continueClick) {
        isCreateNewOrder
          ? this.createNewOrder(recentOrderViewModel, true)
          : this.modifyRecentOrder(recentOrderViewModel);
      }
    });
    return await modal.present();
  }

  handleScoopPreviewBannerClick() {
    this.productAnalyticsService.trackScoopPreviewBannerClick();
    this.routeTo(PATHS.SCOOP);
  }

  fetchAndLoadRecentlyViewed(isRecentlyViewedNewModelEnabled: boolean) {
    if (isRecentlyViewedNewModelEnabled) {
      this.personalizationService
        .fetchPersonalization(PERSONALIZATION_TYPE.recentlyViewed)
        .pipe(
          switchMap(isEnabled =>
            isEnabled
              ? this.personalizationService.getPersonalizationProductNumbers$(
                  PERSONALIZATION_TYPE.recentlyViewed,
                )
              : of(null),
          ),
          filter(productNumbers => !!productNumbers),
          take(1),
        )
        .subscribe(productNumbers => {
          if (productNumbers?.length > 0) {
            this.productService.loadProducts(productNumbers);
          }
        });
    }
  }
}
